import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Field, FieldArray, Form, Formik, FormikErrors } from 'formik';
import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  Catalogue,
  CatalogueItem,
  ConfirmModal,
  DataStatus,
  HeadingLayout,
  HeadingOne,
  HelpButton,
  InvisibleAutoSubmitButton,
  IPaymentGatewayService,
  isDeviceInCart,
  isOnlyDeviceSelected,
  isOnlyDevicesSelected,
  isTenderType,
  isVirtualDevice,
  mapPaymentMethodCode,
  PageLayout,
  PaymentMethodCode,
  PriceType,
  removePlusFromMsisdn,
  ScrollTop,
  SolutionType,
  useWizardEffect,
  WizardContextProvider,
} from '../../../common';
import { ApplicationContext } from '../../context';
import { SolutionSummaryCardInput } from '../../components/solution-summary-card-input/solution-summary-card-input';
import { Payment, PaymentStatus, PaymentTypes } from '../../../common/types/application/payment';
import { FullPageLoader, ShowIf, TapOnPhoneModal } from 'src/components';
import { DeliveryAddressForm } from '../../components/delivery-address-form/delivery-address-form';
import { useAsyncDispatch } from '../../hooks/use-async-dispatch';
import { getApplicationAPIFromApplication } from '../../../common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { QuantityUpdateConfirmModal } from '../../components/quantity-update-confirm-modal/quantity-update-confirm-modal';
import { getSolutionsDeliveryBreakdown } from '../../helpers/delivery-helpers';
import { ConfirmDialogHelper } from '../../../common/components/confirm-dialog-helper/confirm-dialog-helper';
import { getCatalogueStats } from '../../../common/helpers/get-catalogue-stats';
import { FormikHelpers } from 'formik/dist/types';
import {
  formatCurrencyAmount,
  getAmountExcludingVAT,
  getAmountOfVAT,
  getTotalPriceInRands,
} from '../../../common/helpers/currency';
import { convertToAutomationId } from 'src/helpers/functional-helpers';
import {
  CONFIRM_MODAL_HEADING_NO_PAYMENT,
  CONFIRM_MODAL_HEADING_PAYMENT_REQUIRED,
  CONFIRM_MODAL_PRIMARY_BUTTON_TEXT_NO_PAYMENT,
  CONFIRM_MODAL_PRIMARY_BUTTON_TEXT_PAYMENT_REQUIRED,
  CONFIRM_MODAL_SUB_HEADING_NO_PAYMENT,
  getConfirmModalSubheadingPaymentRequired,
} from './resources/headings-and-labels';
import {
  ActionType,
  HelpButtonAction,
  LinkName,
  PageName,
} from '../../services/order-tealium-services';
import { PAGE_NAME } from 'src/features/common/types/tealium-service';
import { FormikChangeListener } from '../../components/product-quantity-change-listener/product-quantity-change-listener';
import { notify, Toast } from 'src/features/common/components/toast/toast';
import { DEVICE_NAMES } from '../../tech-debt/product-details-extra';
import { ErrorCodeTooltip } from 'src/features/common/components/error-code-tooltip/error-code-tooltip';
import {
  Col,
  Colors,
  Device,
  Text,
  privacyNoticeUrl,
  selfOnboardingSalesOrg,
  vodalendDigitalTerms,
} from 'src/common';
import { CreditCard, HorizontalEllipsis, InfoImage } from 'src/assets/images';
import { PaymentOptionItem } from 'src/features/order/pages/order-pre-submit-summary/components';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { useDeliveryDetails } from '../../hooks/use-delivery-details';
import { incrementOrderReferenceNumber } from '@vfs-digital-channels/ns-utils';
import { onboardingProcessPaymentRoute } from 'src/routing/routes';
import { stripWhitespace } from 'src/helpers/formatting';
import { RECEIPT_CHANNEL, RemotePaymentService } from '../../services/remote-payment-service';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { Environment } from 'src/Environment';

import 'react-toastify/dist/ReactToastify.css';
import './order-pre-submit-summary.scss';
import { useApplication } from 'src/hooks/useApplication';

type FormValues = {
  catalogue: Catalogue;
  confirmModalVisible: boolean;
  userRequestedSubmit: boolean;
  userApprovedSubmit: boolean;
  paymentRequired: boolean;
  paymentType: PaymentTypes;
};

export function OrderPreSubmitSummary() {
  const {
    dispatch,
    application,
    paymentGatewayService,
    catalogue,
    salesforceService,
    orderTealiumService,
  } = useContext(ApplicationContext);
  const [paymentType, setPaymentType] = useState(
    application.payment?.paymentType || PaymentTypes.NONE,
  );
  const [showTapOnPhoneModal, setShowTapOnPhoneModal] = React.useState(false);
  const [showPaymentOptions] = React.useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodCode | undefined>(
    undefined,
  );
  const [hasDeliver, setHasDeliver] = useState<boolean>(true);
  const dispatchAsync = useAsyncDispatch(application, dispatch);
  const deliveryDetails = useDeliveryDetails(application, catalogue);
  const handleTechnicalError = useTechnicalErrorHandler();

  const isOnlyPaymentRequestOrGateway = useMemo(
    () =>
      isOnlyDevicesSelected(catalogue, [
        DEVICE_NAMES.VODAPAY_PAYMENT_REQUEST,
        DEVICE_NAMES.VODAPAY_ECOM_GATEWAY,
      ]),
    [catalogue],
  );

  const isOnlyVirtualProducts = useMemo((): boolean => {
    const catalogueProducts = catalogue.products.filter((product) => product.device.quantity > 0);
    return catalogueProducts.every((product) => isVirtualDevice(product.device.title));
  }, [catalogue]);

  const remotePaymentEnabled = useMemo(
    (): boolean => !!application.remotePaymentsEnabled,
    [application.remotePaymentsEnabled],
  );

  const paymentMethodCodeEnabled = useMemo((): boolean => {
    return !Environment.current.disabledFeatures.paymentMethodCode;
  }, []);

  const isBrokerJourney = useMemo((): boolean => !!application.brokerLogin, []);

  const vodapayGatewayEnabled = React.useMemo(
    (): boolean => !Environment.current.disabledFeatures.vodapayGateway,
    [],
  );

  const showLoader = React.useMemo((): boolean => {
    if (vodapayGatewayEnabled && !isBrokerJourney) {
      return isPaymentInProgress(application?.payment) && !!application.gatewayPayment?.initiated;
    }

    return (
      (isPaymentInProgress(application?.payment) && !application.remotePayment?.initiated) ||
      catalogue.statusProducts !== DataStatus.LOADED
    );
  }, [
    application.payment,
    catalogue.statusProducts,
    application.gatewayPayment,
    application.remotePayment,
  ]);

  useEffect(() => {
    const catalogueValues = {
      ...catalogue,
      products: catalogue.products
        .filter(
          (product) => !product.device.productCategoryName || product.device.isProductCategory,
        )
        .filter(({ device }) => device.quantity > 0),
    };

    let productList: CatalogueItem[] = catalogueValues.products;

    orderTealiumService?.onOrderSummaryStart(productList, application.referenceNumber);
  }, []);

  const initialFromValues: FormValues = useMemo(() => {
    const catalogueValues = {
      ...catalogue,
      products: catalogue.products.filter(
        ({ device }) => device.quantity > 0 && !isTenderType(device),
      ),
    };

    return {
      catalogue: catalogueValues,
      confirmModalVisible: false,
      userApprovedSubmit: false,
      userRequestedSubmit: false,
      paymentRequired: (catalogue?.totals?.onceOff || 0) > 0,
      paymentType: application.payment?.paymentType || paymentType,
    };
  }, [catalogue]);

  useEffect(() => {
    const defaultAddresses = (application?.thirdPartyDefaultAddressSolutions ?? []).some(
      (solution) => solution.deliveryOption === DeliveryOptions.Deliver,
    );
    const customAddress = (deliveryDetails?.customAddresses ?? []).some((address) => {
      // Deliver option was not updated on assign device i.e retrieve the default (catalogue)
      if (!address.deliveryOption) {
        return catalogue.products.some((product) => {
          const item = address.solutions.find(
            (solution) => solution.productId === product.device.id,
          );
          return item && item.deliveryOption === DeliveryOptions.Deliver;
        });
      }
      return address.deliveryOption === DeliveryOptions.Deliver;
    });

    setHasDeliver(defaultAddresses || customAddress);
  }, [application, deliveryDetails]);

  const {
    setLoadingOverlayVisible,
    setOnNextEventHandler,
    setNextButtonLabel,
    setNextButtonEnabled,
  } = useWizardEffect(() => {
    const paymentRequired = (catalogue?.totals?.onceOff || 0) > 0;

    return {
      nextButtonEnabled:
        isOnlyVirtualProducts ||
        (!application.agentJourney && !application.brokerLogin) ||
        !paymentRequired ||
        paymentType !== PaymentTypes.NONE,
      nextButtonLabel: paymentRequired ? 'Continue to payment' : 'Complete application',
    };
  }, [application, catalogue]);

  const getTotalProducts = (catalogue: Catalogue) =>
    catalogue.products.reduce<number>((currentTotal, product) => {
      return product.device.quantity ? currentTotal + product.device.quantity : currentTotal;
    }, 0);

  const submit = async (values: FormValues, { setFieldValue }: FormikHelpers<FormValues>) => {
    const isOnlyTapOnPhone = isOnlyDeviceSelected(values.catalogue, DEVICE_NAMES.TAP_ON_PHONE);
    // Only true when user has passed in tap_on_phone_id in query params,
    // ie. only when inside HaloDot app or mocking up halodot from browser
    const isTapOnPhoneJourney: boolean = !!application.tapOnPhoneJourney;

    if (
      isOnlyTapOnPhone &&
      application.solutionType !== SolutionType.thirdPartyOnboarding &&
      values.userRequestedSubmit &&
      !isTapOnPhoneJourney
    ) {
      setShowTapOnPhoneModal(true);
      return;
    }

    const actions: ApplicationAction[] = [];

    values.catalogue.products.forEach((product) => {
      if (product.device.quantity !== product.device.quantityOld) {
        if (product.device.quantity === 0) {
          actions.push({
            type: ApplicationActionType.REMOVE_BASKET_QUANTITY,
            productId: product.device.id,
            acquiringChannel: product.device.acquiringChannel,
            catalogue: catalogue,
          });
        } else {
          actions.push({
            type: ApplicationActionType.UPDATE_BASKET_QUANTITY,
            productId: product.device.id,
            acquiringChannel: product.device.acquiringChannel,
            quantity: product.device.quantity,
            catalogue: catalogue,
          });
        }
      }
    });

    const paymentMethodActions: ApplicationAction[] = values.catalogue.products
      .filter((product) => product.device.quantity > 0)
      .map((product) => {
        const solution = application.opportunity?.solutions?.find(
          (sol) =>
            sol.productId === product.device.id &&
            sol.acquiringChannel === product.device.acquiringChannel,
        );
        if (!solution) {
          throw new Error(
            `Order summary(paymentMethodActions): unknown product: product id: ${product.device.id}`,
          );
        }
        const code =
          solution.paymentMethodCode !== null && solution.paymentMethodCode !== undefined
            ? solution.paymentMethodCode
            : mapPaymentMethodCode(
                product.device.acquiringChannel,
                product.device.allowedPaymentMethods,
                remotePaymentEnabled,
                isBrokerJourney,
              )!;

        return {
          type: ApplicationActionType.ADD_PAYMENT_METHOD_CODE,
          productId: product.device.id,
          acquiringChannel: product.device.acquiringChannel,
          catalogue: catalogue,
          cart: application.cart ?? [],
          cartId: '',
          paymentMethodCode:
            selectedPaymentMethod && !isVirtualDevice(product.device.title)
              ? selectedPaymentMethod
              : code,
        };
      });
    paymentMethodActions.forEach((action) => actions.push(action));

    if (application.brokerLogin && values.paymentType) {
      orderTealiumService?.onPaymentSelected(values.paymentType, LinkName.HOW_WILL_CUSTOMER_PAY);

      if (values.paymentType === PaymentTypes.ONLINE) {
        orderTealiumService?.onPaymentSelected(values.paymentType, LinkName.PAY_NOW);
      }
    }

    if (values.userRequestedSubmit) {
      if (
        (application.agentJourney && values.paymentType === PaymentTypes.OTHER) ||
        (application.brokerLogin && !remotePaymentEnabled) ||
        (application.brokerLogin &&
          remotePaymentEnabled &&
          values.paymentType === PaymentTypes.OTHER)
      ) {
        setFieldValue('userRequestedSubmit', false);

        if (!paymentMethodCodeEnabled) {
          dispatch({
            type: ApplicationActionType.MULTIPLE_ACTIONS,
            actions: [
              {
                type: ApplicationActionType.UPDATE_PAYMENT_TYPE,
                paymentType: values.paymentType,
                onlinePaymentRequired: false,
              },
              { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP },
            ],
          });
          return;
        }

        setLoadingOverlayVisible(true);
        return await dispatchAsync({
          type: ApplicationActionType.MULTIPLE_ACTIONS,
          actions: paymentMethodActions,
        }).then((applicationNew) => {
          return salesforceService
            .saveApplication(getApplicationAPIFromApplication(applicationNew, catalogue), true)
            .then(() => {
              setLoadingOverlayVisible(false);
              dispatch({
                type: ApplicationActionType.MULTIPLE_ACTIONS,
                actions: [
                  {
                    type: ApplicationActionType.UPDATE_PAYMENT_TYPE,
                    paymentType: values.paymentType,
                    onlinePaymentRequired: false,
                  },
                  { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP },
                ],
              });
            });
        });
      }

      if (!values.confirmModalVisible) {
        setFieldValue('confirmModalVisible', true);
        return dispatchAsync({
          type: ApplicationActionType.MULTIPLE_ACTIONS,
          actions,
        });
      } else if (values.userApprovedSubmit) {
        setFieldValue('userRequestedSubmit', false);
        const paymentRequired = (catalogue?.totals?.onceOff || 0) > 0;

        if (
          application.brokerLogin &&
          paymentRequired &&
          values.paymentType === PaymentTypes.ONLINE
        ) {
          setLoadingOverlayVisible(true);

          return dispatchAsync({
            type: ApplicationActionType.UPDATE_PAYMENT_TYPE,
            paymentType: values.paymentType,
            onlinePaymentRequired: true,
          }).then((app) => {
            return salesforceService
              .saveApplication(getApplicationAPIFromApplication(app, catalogue), true)
              .then(() => {
                setLoadingOverlayVisible(false);
                dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
              });
          });
        }

        if (paymentRequired) {
          actions.push({
            type: ApplicationActionType.UPDATE_PAYMENT_STATUS,
            paymentStatus: PaymentStatus.ORDER_IN_PROGRESS,
          });

          setLoadingOverlayVisible(true);
          return dispatchAsync({
            type: ApplicationActionType.MULTIPLE_ACTIONS,
            actions,
          })
            .then((applicationNew) =>
              vodapayGatewayEnabled
                ? reserveFundsV2(applicationNew, dispatch, catalogue)
                : reserveFunds(applicationNew, dispatch, catalogue, paymentGatewayService),
            )
            .catch((error) => {
              setLoadingOverlayVisible(false);
              dispatch({
                type: ApplicationActionType.UPDATE_PAYMENT_STATUS,
                paymentStatus: PaymentStatus.NONE,
              });
              throw error;
            })
            .then(() => setLoadingOverlayVisible(false));
        } else {
          setLoadingOverlayVisible(true);
          return salesforceService
            .saveApplication(getApplicationAPIFromApplication(application, catalogue), true)
            .then(() => {
              setLoadingOverlayVisible(false);
              dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
            });
        }
      }
    } else {
      return dispatchAsync({
        type: ApplicationActionType.MULTIPLE_ACTIONS,
        actions,
      });
    }
  };

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {
      catalogue: {},
    };

    const totalDevices = values.catalogue.products.reduce<number>((currentTotal, product) => {
      return product.device.quantity ? currentTotal + product.device.quantity : currentTotal;
    }, 0);

    if (totalDevices > values.catalogue.maxProductsPerOrder) {
      // @ts-ignore
      errors.catalogue.products = 'total devices per order cannot exceed 70';
    }

    if (totalDevices === 0) {
      // @ts-ignore
      errors.catalogue.products = 'total devices per order must be greater than zero';
    }

    if (errors.catalogue && Object.values(errors.catalogue).length > 0) {
      return errors;
    }

    if (values.paymentType !== PaymentTypes.NONE) {
      setPaymentType(values.paymentType);
      setNextButtonEnabled(true);
    }

    if (values.paymentType === PaymentTypes.ONLINE) {
      setNextButtonLabel('Pay now');
    }

    return {};
  };

  return (
    <section className="order-summary-page">
      <WizardContextProvider>
        <PageLayout>
          <ScrollTop />
          {showTapOnPhoneModal && <TapOnPhoneModal onClose={() => setShowTapOnPhoneModal(false)} />}

          <FullPageLoader showLoader={showLoader} />

          <ShowIf condition={catalogue.statusProducts === DataStatus.LOADED}>
            <Formik<FormValues>
              innerRef={(formik) =>
                formik &&
                setOnNextEventHandler(() => {
                  formik.setValues({
                    ...formik.values,
                    userRequestedSubmit: true,
                  });
                  formik.submitForm();
                })
              }
              initialValues={initialFromValues}
              validate={validate}
              enableReinitialize={true}
              validateOnChange={true}
              onSubmit={submit}
            >
              {({ values, setValues, setFieldValue }) => {
                const confirmLabel = values.paymentRequired
                  ? CONFIRM_MODAL_PRIMARY_BUTTON_TEXT_PAYMENT_REQUIRED
                  : CONFIRM_MODAL_PRIMARY_BUTTON_TEXT_NO_PAYMENT;

                const paymentRequired = (catalogue?.totals?.onceOff || 0) > 0;

                values.confirmModalVisible &&
                  (values.paymentRequired
                    ? orderTealiumService?.onShowReserveFundModal(application.referenceNumber)
                    : orderTealiumService?.onShowConfirmModal(application.referenceNumber));

                return (
                  <Form
                    className={`order-summary-form ${
                      application.brokerLogin ? 'broker-container' : ''
                    }`}
                  >
                    {application.solutionType === SolutionType.thirdPartyOnboarding &&
                      paymentRequired &&
                      !isOnlyVirtualProducts && (
                        <div className="payment-option">
                          <HeadingLayout
                            renderMainHeading={() => (
                              <HeadingOne>How will the customer be paying?</HeadingOne>
                            )}
                          />

                          <div className="payment-option-button">
                            {remotePaymentEnabled && (
                              <Field
                                type="radio"
                                name="paymentType"
                                value={PaymentTypes.ONLINE}
                                label="Online Payment"
                                icon={CreditCard}
                                component={PaymentOptionItem}
                                onClick={() => {
                                  if (!paymentMethodCodeEnabled) return;
                                  setSelectedPaymentMethod(PaymentMethodCode.ONLINE_PAYMENT);
                                }}
                                light
                              />
                            )}

                            {(!hasDeliver || !remotePaymentEnabled) && (
                              <Field
                                type="radio"
                                name="paymentType"
                                value={PaymentTypes.OTHER}
                                label="Other"
                                icon={HorizontalEllipsis}
                                component={PaymentOptionItem}
                                onClick={() => {
                                  if (!paymentMethodCodeEnabled) return;
                                  setSelectedPaymentMethod(PaymentMethodCode.CASH);
                                }}
                                light
                              />
                            )}
                          </div>
                        </div>
                      )}

                    {showPaymentOptions &&
                      ((application.solutionType === SolutionType.thirdPartyOnboarding &&
                        paymentRequired) ||
                        (application.solutionType === SolutionType.thirdPartyOnboarding &&
                          application.agentJourney)) &&
                      !remotePaymentEnabled &&
                      selectedPaymentMethod && (
                        <>
                          <HeadingLayout
                            renderMainHeading={() => <HeadingOne>Order summary</HeadingOne>}
                          />

                          {(application.brokerLogin || application.agentJourney) &&
                            !isOnlyVirtualProducts && (
                              <Col
                                css={`
                                  @media screen and (min-width: ${Device.Tablet}) {
                                    width: 100%;
                                  }
                                `}
                              >
                                <ErrorCodeTooltip
                                  pageName={PAGE_NAME.ORDER_COMPLETE}
                                  showErrorCodes={false}
                                  orientation={'bottom'}
                                  content={[
                                    {
                                      code: '001',
                                      name: 'Delivery',
                                      hint: 'Devices are shipped free within 5 business days on application approval and can be delivered to multiple addresses.',
                                    },
                                  ]}
                                >
                                  <Text
                                    gap={'7px'}
                                    display={'flex'}
                                    fontWeight={400}
                                    fontSize={'16px'}
                                    margin={'21px 0 0 0'}
                                    icon={InfoImage}
                                    cursor={'pointer'}
                                    zIndex={100}
                                    color={Colors.Black}
                                    textDecoration={'underline'}
                                  >
                                    {'When will my order be delivered?'}
                                  </Text>
                                </ErrorCodeTooltip>
                              </Col>
                            )}

                          {(application.brokerLogin || application.agentJourney) && <hr />}
                          <FormikChangeListener<FormValues>>
                            {(oldValues, newValues) => {
                              newValues.catalogue.products.forEach((product, index) => {
                                let oldValue = oldValues.catalogue.products[index].device.quantity;

                                if (
                                  product.device.quantity > oldValue ||
                                  (product.device.quantity !== undefined && oldValue === undefined)
                                ) {
                                  notify({
                                    children: 'Product added to cart',
                                    autoClose: 5000,
                                  });
                                }
                              });

                              const productItems = newValues.catalogue.products
                                .filter((product) => product.device.quantity > 0)
                                .map((product) => product);

                              dispatch({
                                type: ApplicationActionType.SHOW_DEVICE_ACTIVATION,
                                productItems,
                              });

                              if (
                                getTotalProducts(oldValues.catalogue) !==
                                  oldValues.catalogue.maxProductsPerOrder &&
                                getTotalProducts(newValues.catalogue) ===
                                  newValues.catalogue.maxProductsPerOrder
                              ) {
                                notify({
                                  children: 'Maximum 70 products allowed per order',
                                  autoClose: 5000,
                                });
                              }
                            }}
                          </FormikChangeListener>
                          <Toast />
                          <ConfirmModal
                            visible={values.confirmModalVisible}
                            heading={
                              values.paymentRequired
                                ? CONFIRM_MODAL_HEADING_PAYMENT_REQUIRED
                                : CONFIRM_MODAL_HEADING_NO_PAYMENT
                            }
                            subtext={
                              values.paymentRequired
                                ? getConfirmModalSubheadingPaymentRequired(
                                    values.catalogue?.totals?.onceOff || 0,
                                  )
                                : CONFIRM_MODAL_SUB_HEADING_NO_PAYMENT
                            }
                            confirmButtonLabel={confirmLabel}
                            onCancel={() => {
                              setValues({
                                ...values,
                                confirmModalVisible: false,
                                userRequestedSubmit: false,
                                userApprovedSubmit: false,
                              });
                              values.paymentRequired
                                ? orderTealiumService?.onActionReserveFundModal(ActionType.CANCEL)
                                : orderTealiumService?.onActionConfirmFundModal(ActionType.CANCEL);
                            }}
                            onConfirm={() =>
                              setValues({
                                ...values,
                                userApprovedSubmit: true,
                              })
                            }
                            automationIdPrefix="Revamp_Order_Summary_Confirm_Submission"
                          />

                          <FieldArray
                            name="products"
                            render={() => (
                              <RenderSolutions
                                catalogueSolutions={values.catalogue}
                                setFieldValue={setFieldValue}
                                catalogue={catalogue}
                                dispatch={dispatch}
                                application={application}
                                isOnlyPaymentRequestOrGateway={isOnlyPaymentRequestOrGateway}
                              />
                            )}
                          />
                          {!isOnlyPaymentRequestOrGateway && renderPriceTotals(values.catalogue)}
                          <InvisibleAutoSubmitButton />

                          <>
                            {!isOnlyPaymentRequestOrGateway ? (
                              <section className="address-section">
                                <DeliveryAddressForm
                                  application={application}
                                  dispatch={dispatch}
                                  catalogue={catalogue}
                                  automationIdSuffix="Revamp_Order_Summary_Delivery_Address_Form"
                                />
                              </section>
                            ) : (
                              <section className="please-note-section">
                                <hr />
                                <h5>
                                  <strong>Please note:</strong> You only pay per transaction
                                </h5>
                                <hr />
                              </section>
                            )}
                          </>
                        </>
                      )}

                    {((!application.brokerLogin && !application.agentJourney) ||
                      (!!values.paymentType && remotePaymentEnabled) ||
                      !values.paymentRequired ||
                      (application.agentJourney &&
                        (values.paymentType || isOnlyVirtualProducts))) && (
                      <>
                        <HeadingLayout
                          renderMainHeading={() => <HeadingOne>Order summary</HeadingOne>}
                        />

                        {(application.brokerLogin || application.agentJourney) &&
                          !isOnlyVirtualProducts && (
                            <Col
                              css={`
                                @media screen and (min-width: ${Device.Tablet}) {
                                  width: 100%;
                                }
                              `}
                            >
                              <ErrorCodeTooltip
                                pageName={PAGE_NAME.ORDER_COMPLETE}
                                showErrorCodes={false}
                                orientation={'bottom'}
                                content={[
                                  {
                                    code: '001',
                                    name: 'Delivery',
                                    hint: 'Devices are shipped free within 5 business days on application approval and can be delivered to multiple addresses.',
                                  },
                                ]}
                              >
                                <Text
                                  gap={'7px'}
                                  display={'flex'}
                                  fontWeight={400}
                                  fontSize={'16px'}
                                  margin={'21px 0 0 0'}
                                  icon={InfoImage}
                                  cursor={'pointer'}
                                  zIndex={100}
                                  color={Colors.Black}
                                  textDecoration={'underline'}
                                >
                                  {'When will my order be delivered?'}
                                </Text>
                              </ErrorCodeTooltip>
                            </Col>
                          )}

                        {(application.brokerLogin || application.agentJourney) && <hr />}
                        <FormikChangeListener<FormValues>>
                          {(oldValues, newValues) => {
                            newValues.catalogue.products.forEach((product, index) => {
                              let oldValue = oldValues.catalogue.products[index].device.quantity;

                              if (
                                product.device.quantity > oldValue ||
                                (product.device.quantity !== undefined && oldValue === undefined)
                              ) {
                                notify({
                                  children: 'Product added to cart',
                                  autoClose: 5000,
                                });
                              }
                            });

                            const productItems = newValues.catalogue.products
                              .filter((product) => product.device.quantity > 0)
                              .map((product) => product);

                            dispatch({
                              type: ApplicationActionType.SHOW_DEVICE_ACTIVATION,
                              productItems,
                            });

                            if (
                              getTotalProducts(oldValues.catalogue) !==
                                oldValues.catalogue.maxProductsPerOrder &&
                              getTotalProducts(newValues.catalogue) ===
                                newValues.catalogue.maxProductsPerOrder
                            ) {
                              notify({
                                children: 'Maximum 70 products allowed per order',
                                autoClose: 5000,
                              });
                            }
                          }}
                        </FormikChangeListener>
                        <Toast />

                        <ConfirmModal
                          visible={values.confirmModalVisible}
                          heading={
                            values.paymentRequired
                              ? CONFIRM_MODAL_HEADING_PAYMENT_REQUIRED
                              : CONFIRM_MODAL_HEADING_NO_PAYMENT
                          }
                          subtext={
                            values.paymentRequired
                              ? getConfirmModalSubheadingPaymentRequired(
                                  values.catalogue?.totals?.onceOff || 0,
                                )
                              : CONFIRM_MODAL_SUB_HEADING_NO_PAYMENT
                          }
                          confirmButtonLabel={confirmLabel}
                          onCancel={() => {
                            setValues({
                              ...values,
                              confirmModalVisible: false,
                              userRequestedSubmit: false,
                              userApprovedSubmit: false,
                            });
                            values.paymentRequired
                              ? orderTealiumService?.onActionReserveFundModal(ActionType.CANCEL)
                              : orderTealiumService?.onActionConfirmFundModal(ActionType.CANCEL);
                          }}
                          onConfirm={() => {
                            setValues({
                              ...values,
                              userApprovedSubmit: true,
                            });

                            values.paymentRequired
                              ? orderTealiumService?.onActionReserveFundModal(ActionType.RESERVE)
                              : orderTealiumService?.onActionConfirmFundModal(ActionType.CONFIRM);
                          }}
                          automationIdPrefix="Revamp_Order_Summary_Confirm_Submission"
                        />

                        <FieldArray
                          name="products"
                          render={() => (
                            <RenderSolutions
                              catalogueSolutions={values.catalogue}
                              setFieldValue={setFieldValue}
                              catalogue={catalogue}
                              dispatch={dispatch}
                              application={application}
                              isOnlyPaymentRequestOrGateway={isOnlyPaymentRequestOrGateway}
                            />
                          )}
                        />
                        {!isOnlyVirtualProducts && renderPriceTotals(values.catalogue)}
                        <InvisibleAutoSubmitButton />

                        {(application.brokerLogin ||
                          application.agentJourney ||
                          application.solutionType !== SolutionType.buyInStoreFicaOnline) && (
                          <>
                            {!isOnlyVirtualProducts ? (
                              <section className="address-section">
                                <DeliveryAddressForm
                                  application={application}
                                  dispatch={dispatch}
                                  catalogue={catalogue}
                                  automationIdSuffix="Revamp_Order_Summary_Delivery_Address_Form"
                                />
                              </section>
                            ) : (
                              <section className="please-note-section">
                                <hr />
                                <h5>
                                  <strong>Please note:</strong> You only pay per transaction
                                </h5>
                                <hr />
                              </section>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Form>
                );
              }}
            </Formik>

            <section className="please-note-section">
              <p>
                By continuing, I agree to the Vodacom digital terms &{' '}
                <a href={vodalendDigitalTerms}>merchant agreement.</a>
              </p>
              <p className="has-margin-top-6">
                View our&nbsp;
                <a href={privacyNoticeUrl} target="_blank" rel="noreferrer">
                  Privacy Notice.
                </a>
              </p>
            </section>
          </ShowIf>
        </PageLayout>
      </WizardContextProvider>
      <HelpButton
        onOpen={() =>
          orderTealiumService?.onHelpButtonActions(PageName.ORDER_SUMMARY, HelpButtonAction.CLICK)
        }
        onEmailClicked={() =>
          orderTealiumService?.onHelpButtonActions(PageName.ORDER_SUMMARY, HelpButtonAction.EMAIL)
        }
        onPhoneNumberClicked={() =>
          orderTealiumService?.onHelpButtonActions(PageName.ORDER_SUMMARY, HelpButtonAction.CALL)
        }
      />
    </section>
  );

  function reserveFunds(
    application: Application,
    dispatch: React.Dispatch<ApplicationAction>,
    catalogue: Catalogue,
    paymentGatewayService: IPaymentGatewayService,
  ): Promise<void> {
    const mobileNumber = application?.contact?.mobileNumber;
    const idNumber = application?.contact?.idNumber;
    const total = getTotalPriceInRands(catalogue?.totals?.onceOff || 0);

    if (mobileNumber && idNumber && total > 0) {
      return salesforceService
        .saveApplication(getApplicationAPIFromApplication(application, catalogue), true)
        .then(() => {
          const abortController = new AbortController();
          return paymentGatewayService
            .createOrder(mobileNumber, idNumber, total, abortController)
            .then((orderResult) => {
              dispatch({
                type: ApplicationActionType.UPDATE_PAYMENT_STATUS,
                paymentStatus: PaymentStatus.PAYMENT_IN_PROGRESS,
                orderNumber: orderResult.orderNumber,
              });
              return paymentGatewayService.reserveFunds(orderResult.orderNumber, abortController);
            });
        });
    } else {
      throw new Error(
        'Order Summary revamp: mobile number, id number and non-zero amount required for payment gateway',
      );
    }
  }

  async function reserveFundsV2(
    application: Application,
    dispatch: React.Dispatch<ApplicationAction>,
    catalogue: Catalogue,
  ): Promise<void> {
    const mobileNumber = application?.contact?.mobileNumber;
    const idNumber = application?.contact?.idNumber;
    const email = application?.contact?.emailAddress;
    const total = catalogue?.totals?.onceOff || 0;

    if (mobileNumber && idNumber && total > 0) {
      const basket = catalogue.products
        .filter(
          (product) =>
            product.device.quantity > 0 && product.price.priceType === PriceType.ONCE_OFF,
        )
        .map((product, index) => {
          return {
            lineNumber: `${index}`,
            itemId: product.device.title,
            barcode: product.device.id,
            quantity: product.device.quantity,
            description: product.device.title,
            amountExVAT: getAmountExcludingVAT(product.price.price || 0),
            amountVAT: getAmountOfVAT(product.price.price || 0),
          };
        });

      const orderNumber = incrementOrderReferenceNumber(
        application.referenceNumber || '',
        application.gatewayPayment?.numberOfTries || 0,
      );

      const paymentDetails = {
        salesOrganization: selfOnboardingSalesOrg,
        totalAmount: total,
        orderReferenceNumber: application.referenceNumber || '',
        traceId: orderNumber,
        basket,
        callbackUrl: `${Environment.current.uiHost}${onboardingProcessPaymentRoute}`,
        contactName: application.contact?.name || '',
        email,
        msisdn: removePlusFromMsisdn(stripWhitespace(mobileNumber)),
        receiptChannel: RECEIPT_CHANNEL.EMAIL,
      };

      const abortController = new AbortController();

      dispatch({
        type: ApplicationActionType.UPDATE_PAYMENT_STATUS,
        paymentStatus: PaymentStatus.PAYMENT_IN_PROGRESS,
        orderNumber,
      });

      return RemotePaymentService.initiatePayment(
        paymentDetails,
        abortController,
        handleTechnicalError,
      )
        .then(({ initiationUrl, transactionId }) => {
          if (initiationUrl && transactionId) {
            return dispatchAsync({
              type: ApplicationActionType.SET_GATEWAY_PAYMENT_STATUS,
              initiated: true,
              transactionId,
              numberOfTries: application.gatewayPayment?.numberOfTries || 0,
            }).then((applicationNew) => {
              return salesforceService
                .saveApplication(getApplicationAPIFromApplication(applicationNew, catalogue), true)
                .then(() => {
                  window.location.replace(initiationUrl);
                  dispatch({
                    type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP,
                  });
                });
            });
          }
          throw new Error('initiationUrl and transactionId not found');
        })
        .catch((error) => {
          console.error(error);
          throw new Error(
            `Order Summary revamp: VodaPay Gateway: initiatePayment failed: ${error} `,
          );
        });
    } else {
      throw new Error(
        'Order Summary revamp: mobile number, id number and non-zero amount required for payment gateway',
      );
    }
  }

  function isPaymentInProgress(payment?: Payment) {
    switch (payment?.paymentStatus) {
      case PaymentStatus.PAYMENT_IN_PROGRESS:
      case PaymentStatus.ORDER_IN_PROGRESS:
        return true;
      default:
        return false;
    }
  }

  function renderPriceTotals(catalogue: Catalogue) {
    return (
      <>
        <section className={'grid'}>
          <p className="delivery-text">Delivery</p>
          <p className="delivery-text">
            <b>Free</b>
          </p>

          {catalogue.products.find((product) => product.device.title === 'VodaPay Max') && (
            <>
              <p className="delivery-text">VodaPay Vending</p>
              <p className="delivery-text">
                <b>Free</b>
              </p>
            </>
          )}

          <p>Once-off</p>
          <p>
            <b>R {formatCurrencyAmount(catalogue.totals.onceOff || 0)}</b>
          </p>

          <p>Monthly</p>
          <p>
            <b>R {formatCurrencyAmount(catalogue.totals.monthly || 0)}</b>
          </p>

          <ShowIf
            condition={catalogue.totals.monthly !== undefined && catalogue.totals.monthly > 0}
          >
            <p>
              <b>Please note:</b> A monthly payment of R
              {formatCurrencyAmount(catalogue.totals.monthly || 0)} applies once you start
              transacting on this device
            </p>
          </ShowIf>
        </section>
      </>
    );
  }
}

type RenderSolutionsProps = {
  catalogueSolutions: Catalogue;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  catalogue: Catalogue;
  dispatch: React.Dispatch<ApplicationAction>;
  application: Application;
  isOnlyPaymentRequestOrGateway: boolean;
};

function RenderSolutions({
  catalogueSolutions,
  setFieldValue,
  catalogue,
  dispatch,
  application,
  isOnlyPaymentRequestOrGateway,
}: RenderSolutionsProps) {
  const { orderTealiumService } = useApplication();
  const [removeSolutionCount, setRemoveSolutionCount] = useState<number>(0);
  const [itemRemoved, setItemRemoved] = useState<CatalogueItem>({} as CatalogueItem);

  const totalQuantity = catalogueSolutions.products.reduce(
    (currentTotal, product) => currentTotal + product.device.quantity,
    0,
  );

  const totalNumberOfProducts = catalogueSolutions.products.filter(
    (catalogueItem) => catalogueItem.device.quantity > 0,
  ).length;

  useEffect(() => {
    if (removeSolutionCount > 0 && Object.keys(itemRemoved).length > 0) {
      const actions: ApplicationAction[] = [
        {
          type: ApplicationActionType.REMOVE_BASKET_QUANTITY,
          productId: itemRemoved.device.id,
          acquiringChannel: itemRemoved.device.acquiringChannel,
          catalogue: catalogue,
        },
      ];

      if (
        (!isDeviceInCart(catalogueSolutions, DEVICE_NAMES.VODAPAY_ECOM_GATEWAY) ||
          !isDeviceInCart(catalogueSolutions, DEVICE_NAMES.VODAPAY_PAYMENT_REQUEST)) &&
        totalNumberOfProducts === 0
      ) {
        actions.push({ type: ApplicationActionType.RESTART });
      }

      dispatch({ type: ApplicationActionType.MULTIPLE_ACTIONS, actions });
    }
  }, [removeSolutionCount, catalogueSolutions, itemRemoved]);

  const handleRemoved = (catalogueItem: CatalogueItem) => {
    setRemoveSolutionCount((prev) => prev + 1);
    setItemRemoved(catalogueItem);
  };

  return (
    <ConfirmDialogHelper>
      {({ confirm, isDialogShown, handleCancel, handleConfirm }) => {
        return (
          <>
            <QuantityUpdateConfirmModal
              isModalVisible={isDialogShown && !application.brokerLogin}
              onModalShow={() => orderTealiumService?.onReduceQuantityModalStart()}
              onCancel={handleCancel}
              onConfirm={() => {
                orderTealiumService?.onReduceQuantityModalAction();
                handleConfirm();
              }}
            />
            {catalogueSolutions.products
              .filter((catalogueItem) => catalogueItem.device.quantity > 0)
              .map((catalogueItem, index) => {
                const maxQuantity =
                  totalQuantity >= catalogueSolutions.maxProductsPerOrder
                    ? catalogueItem.device.quantity || 0
                    : catalogueItem.device.maxQuantity ||
                      catalogueSolutions.maxProductsPerOrder -
                        (totalQuantity - (catalogueItem.device.quantity || 0));
                const minQuantity = totalQuantity <= 1 ? 1 : catalogueItem.device.minQuantity;

                const fieldName = `catalogue.products[${index}].device.quantity`;

                let priceString = '';

                switch (catalogueItem.price.priceType) {
                  case PriceType.FREE:
                    priceString = 'Free';
                    break;
                  case PriceType.ONCE_OFF:
                    priceString = `R ${formatCurrencyAmount(catalogueItem.price.price)} once off`;
                    break;
                  case PriceType.MONTHLY:
                    priceString = `R ${formatCurrencyAmount(catalogueItem.price.price)} pm`;
                    break;
                }

                return (
                  <SolutionSummaryCardInput
                    key={`${catalogueItem.device.title}-${catalogueItem.price.price}-${index}`}
                    name={fieldName}
                    title={catalogueItem.device.title}
                    image={catalogueItem.device.imageBase64 || ''}
                    price={catalogueItem.price.price || 0}
                    priceString={priceString}
                    disableQuantity={!!application.brokerLogin}
                    min={minQuantity || 0}
                    max={maxQuantity}
                    onRemoved={() => handleRemoved(catalogueItem)}
                    showRemoveButton={totalNumberOfProducts > 1 || isOnlyPaymentRequestOrGateway}
                    showInput={!isOnlyPaymentRequestOrGateway}
                    automationid={`Revamp_Order_Summary_Solution_Summary_${convertToAutomationId(
                      catalogueItem.device.title,
                    )}`}
                    onModified={(value) => {
                      const catalogueNew: Catalogue = {
                        ...catalogueSolutions,
                        products: Object.assign([...catalogueSolutions.products], {
                          [index]: {
                            ...catalogueSolutions.products[index],
                            device: {
                              ...catalogueSolutions.products[index].device,
                              quantity: value,
                            },
                          },
                        }),
                      };

                      const catalogueStats = getCatalogueStats(catalogueNew);

                      if ((catalogueStats.deviceTotals?.totalDevices || 0) == 0) {
                        // we stop the user from removing all devices from the device totals on order summary screen
                        return false;
                      }

                      const { solutionsWithTooManyAddresses } = getSolutionsDeliveryBreakdown(
                        application,
                        catalogueNew,
                        true,
                      );

                      if (solutionsWithTooManyAddresses.length > 0) {
                        confirm(() => {
                          setFieldValue(fieldName, value);
                        });
                        return false;
                      }

                      return true;
                    }}
                  />
                );
              })}
          </>
        );
      }}
    </ConfirmDialogHelper>
  );
}
